import React, { useEffect, useRef, useState } from "react"
import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import TextBundle from "aldoo-ra/CMS/text-bundle"

// Dedicated sign-in surface for the AldooSnap mac app WebView
// (window.aldooSnapApp is injected by the app). Chromeless — no navbar or
// footer — and spinner-first: the page never flashes the home layout, it
// shows a loading state until the sign-in dialog takes over.
const postToApp = (auth) => {
  if (!auth?.auth_token) return false
  const handler = window.webkit?.messageHandlers?.aldooSnap
  if (!handler) return false
  handler.postMessage(JSON.stringify(auth))
  return true
}

export default function AppSigninPage() {
  const texts = TextBundle({ id: "app-signin-page" })
  const [auth] = PlatformAuth.useAuth()
  const [mounted, setMounted] = useState(false)
  const posted = useRef(false)

  useEffect(() => setMounted(true), [])

  // Not signed in → open the signin dialog as soon as the dialog stack is
  // mounted (small delay lets AldooBoot register the dialogs).
  useEffect(() => {
    if (!mounted || auth?.auth_token) return
    const timer = setTimeout(
      () => PubSub.publish("signin", { forceDialog: true }),
      150
    )
    return () => clearTimeout(timer)
  }, [mounted, auth])

  // Signed in → deliver the credentials to the app once.
  useEffect(() => {
    if (!auth?.auth_token || posted.current) return
    posted.current = postToApp(auth)
  }, [auth])

  const signedIn = mounted && !!auth?.auth_token

  return (
    <div className="fixed inset-0 z-40 bg-canvas flex flex-col items-center justify-center p-8">
      {signedIn ? (
        <div className="w-full max-w-md flex flex-col items-center text-center gap-4">
          <img
            src="/static/images/logos/app_logo.png"
            alt={texts?.logo_alt}
            className="w-16 h-16"
          />

          <Typography variant="h3">
            {texts?.signed_in_title}
          </Typography>

          <Typography variant="body1_dim">
            {texts?.signed_in_message}
          </Typography>

          <Button
            variant="secondary_m"
            onClick={() => postToApp(auth)}
          >
            {texts?.continue_in_app_button}
          </Button>
        </div>
      ) : (
        // Loading state — visible on first paint and behind the dialog.
        <div className="flex flex-col items-center gap-4">
          <img
            src="/static/images/logos/app_logo.png"
            alt={texts?.logo_alt}
            className="w-16 h-16"
          />

          <Spinner className="w-6 h-6" />

          <Typography variant="body1_dim">
            {texts?.preparing_signin_message}
          </Typography>
        </div>
      )}
    </div>
  )
}
